import { useId } from 'react';
import { Select } from 'radix-ui';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import styled from 'styled-components';

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;
const Label = styled.label`
  && { margin: 0; font-size: 13px; font-weight: 500; }
  color: ${({ theme }) => theme.colors.textSecondary};
`;
const Trigger = styled(Select.Trigger)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-width: 0;
  min-height: 46px;
  padding: 11px 14px;
  border: 1px solid ${({ theme }) => theme.isDarkMode ? '#ffffff1a' : '#0000001a'};
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  > span:first-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  > span:last-child { display: flex; flex-shrink: 0; color: ${({ theme }) => theme.colors.textSecondary}; }
  &:hover:not(:disabled) { border-color: ${({ theme }) => theme.colors.textSecondary}80; }
  &[data-state='open'] { border-color: ${({ theme }) => theme.colors.primary}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.primary}; outline-offset: 3px; }
  &:disabled { opacity: 0.5; cursor: default; }
`;
const Content = styled(Select.Content)`
  z-index: 12000;
  min-width: var(--radix-select-trigger-width);
  max-width: calc(100vw - 32px);
  max-height: min(320px, var(--radix-select-content-available-height));
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.isDarkMode ? '#ffffff1a' : '#0000001a'};
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  box-shadow: 0 12px 36px ${({ theme }) => theme.isDarkMode ? '#00000066' : '#0000001f'};
`;
const Item = styled(Select.Item)`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 42px;
  padding: 10px 12px;
  border-radius: 8px;
  font: inherit;
  font-size: 14px;
  line-height: 1.4;
  cursor: pointer;
  outline: none;
  user-select: none;
  overflow-wrap: anywhere;
  &[data-highlighted] { background: ${({ theme }) => theme.colors.primary}18; }
  &[data-state='checked'] { color: ${({ theme }) => theme.colors.primary}; font-weight: 500; }
`;
const Indicator = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  margin-left: auto;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.primary};
`;
const ScrollButton = styled.div`
  display: flex;
  justify-content: center;
  padding: 4px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export default function SelectField({ label, ariaLabel = label, value, onValueChange, options, disabled, describedBy }) {
  const id = useId();
  return <Field>
    <Label htmlFor={id}>{label}</Label>
    <Select.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <Trigger id={id} aria-label={ariaLabel} aria-describedby={describedBy}>
        <Select.Value />
        <Select.Icon><ChevronDown size={16} aria-hidden="true" /></Select.Icon>
      </Trigger>
      <Select.Portal>
        <Content position="popper" sideOffset={6} collisionPadding={16}>
          <Select.ScrollUpButton asChild><ScrollButton><ChevronUp size={16} /></ScrollButton></Select.ScrollUpButton>
          <Select.Viewport style={{ padding: 5 }}>
            {options.map(option => <Item key={option.value} value={option.value}>
              <Select.ItemText>{option.label}</Select.ItemText>
              <Indicator><Select.ItemIndicator><Check size={16} aria-hidden="true" /></Select.ItemIndicator></Indicator>
            </Item>)}
          </Select.Viewport>
          <Select.ScrollDownButton asChild><ScrollButton><ChevronDown size={16} /></ScrollButton></Select.ScrollDownButton>
        </Content>
      </Select.Portal>
    </Select.Root>
  </Field>;
}
